import isPlainObject from 'is-plain-object';

import {
  AdyenEnvironment,
  isAdyenEnvironment,
} from '../components/Adyen/Forms/AdyenCreditCardForm.static';
import { CreditCardData } from '../components/StoredCreditCards/index.interfaces';
import { isLocale } from '../services/i18nService';
import { captureException } from '../services/sentryService';
import {
  CustomerType,
  Locale,
  Nullable,
  ShopBundle,
  isShopBundle,
  isCustomerType,
  MaybeNullable,
  CreditCardBrand,
} from '../types';

import { base64JsonToJS } from './braintreeUtils';
import { convertCreditCardBrand, normalizeCreditCardBrand } from './cardUtils';
import { ParsedCoreProvidersProps } from './extractFromHtml.types';
import { defaultBoolean, defaultNullString, safeStringify } from './utils';

/*
 * Parsing individual information.
 *
 * Problems while parsing base64-encoded attributes are logged to Sentry during the parsing, in order to provide the decoded value in the message.
 *
 * Problems while parsing readable attributes are not logged to Sentry, because each 'normalize' function will log in one message all the problematic attributes, along with the container's content.
 */

export function nonEmptyString(container: HTMLElement, attributeName: string): Nullable<string> {
  const attribute = container.dataset[attributeName];
  return attribute ? attribute : null;
}

export function checkMyAccountSetting(container: HTMLElement): 'myaccount' | 'default' {
  return container.dataset.myaccount === 'true' ? 'myaccount' : 'default';
}

export function parseLocale(container: HTMLElement): Nullable<Locale> {
  const maybeLocale = container.dataset.locale?.replace('_', '-');
  return isLocale(maybeLocale) ? maybeLocale : null;
}

export function parseShopBundle(container: HTMLElement): Nullable<ShopBundle> {
  const maybeShopBundle = container.dataset.shopBundle;
  return isShopBundle(maybeShopBundle) ? maybeShopBundle : null;
}

export function parseMaybeCustomerType(
  maybeCustomerType: MaybeNullable<string>,
): Nullable<CustomerType> {
  return isCustomerType(maybeCustomerType) ? maybeCustomerType : null;
}

export function parseCustomerType(container: HTMLElement): Nullable<CustomerType> {
  return parseMaybeCustomerType(container.dataset.customerType);
}

export function ensureCreditCardData(possibleCreditCardDatum: unknown): Nullable<CreditCardData> {
  if (isPlainObject(possibleCreditCardDatum)) {
    const plainObject = possibleCreditCardDatum as Record<PropertyKey, unknown>;
    const pspIdentifier = defaultNullString(plainObject.pspIdentifier);

    let expirationMonth = defaultNullString(plainObject.expirationMonth);
    if (expirationMonth && expirationMonth.length === 1) {
      expirationMonth = '0' + expirationMonth;
    }

    return pspIdentifier === null || pspIdentifier === ''
      ? null
      : {
          pspIdentifier,
          holderName: defaultNullString(plainObject.holderName),
          brand: convertCreditCardBrand(plainObject.brand),
          expirationMonth,
          expirationYear: defaultNullString(plainObject.expirationYear),
          selected: defaultBoolean(plainObject.selected),
          expired: defaultBoolean(plainObject.expired),
          lastFourDigits: defaultNullString(plainObject.lastFourDigits),
          nonce: defaultNullString(plainObject.nonce),
        };
  }
  return null;
}

export function parseAdyenCreditCardData(container: HTMLElement): Nullable<CreditCardData[]> {
  const possibleCreditCardData = base64JsonToJS<unknown>(container.dataset.creditCards);

  if (Array.isArray(possibleCreditCardData)) {
    return possibleCreditCardData.reduce(
      (actualCreditCardData: CreditCardData[], possibleCreditCardDatum) => {
        const checkedCreditCardData = ensureCreditCardData(possibleCreditCardDatum);

        if (checkedCreditCardData) {
          // ensure that the selected creditCard is on the front
          if (checkedCreditCardData.selected) {
            actualCreditCardData.unshift(checkedCreditCardData);
          } else {
            actualCreditCardData.push(checkedCreditCardData);
          }
        } else {
          captureException({
            message: `Unexpected data for a creditCard: ${safeStringify(possibleCreditCardDatum)}`,
          });
        }
        return actualCreditCardData;
      },
      [],
    );
  } else {
    captureException({
      message: `Got a non-array as the 'creditCards' attribute: ${safeStringify(
        possibleCreditCardData,
      )}`,
    });
    return null;
  }
}

export function parseAdyenEnvironment(container: HTMLElement): Nullable<AdyenEnvironment> {
  const maybeAdyenEnvironment = container.dataset.adyenEnvironment;
  return isAdyenEnvironment(maybeAdyenEnvironment) ? maybeAdyenEnvironment : null;
}

/**
 * Lenient analysis of the brand codes: if one element of the array is not a string or is not a recognized brand code, we just ignore it instead of returning null. Otherwise, we will block the whole component from rendering because of one irregular value.
 */
export function parsePossibleCreditCardBrands(
  possibleCreditCardBrands: unknown,
): Nullable<CreditCardBrand[]> {
  if (Array.isArray(possibleCreditCardBrands)) {
    return possibleCreditCardBrands.reduce(
      (creditCardBrands: CreditCardBrand[], possibleCreditCardBrand: unknown) => {
        const creditCardBrand = normalizeCreditCardBrand(possibleCreditCardBrand);
        if (creditCardBrand) {
          creditCardBrands.push(creditCardBrand);
        } else {
          captureException({
            message: `Got an unexpected CreditCardBrand: ${possibleCreditCardBrand}`,
          });
        }
        return creditCardBrands;
      },
      [],
    );
  } else {
    captureException({
      message: `Got a non-array for the 'creditCardBrands': ${safeStringify(
        possibleCreditCardBrands,
      )}`,
    });
    return null;
  }
}

export function parseCreditCardBrands(container: HTMLElement): Nullable<CreditCardBrand[]> {
  const possibleAdyenCardBrands = base64JsonToJS<unknown>(container.dataset.supportedBrandCodes);
  return parsePossibleCreditCardBrands(possibleAdyenCardBrands);
}

export function isFiniteNumber(value: number) {
  return !Number.isNaN(value) && Number.isFinite(value);
}

export function parseAmount(container: HTMLElement): Nullable<number> {
  if (container.dataset.amount) {
    const amount = parseFloat(container.dataset.amount);
    return isFiniteNumber(amount) ? amount : null;
  }
  return null;
}

export function getNullKeys<T extends ParsedCoreProvidersProps>(
  record: Record<string, unknown> | T,
) {
  const nullKeys = [];

  for (const [key, value] of Object.entries(record)) {
    if (value === null) {
      nullKeys.push(key);
    }
  }

  return nullKeys;
}
